import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Grid, Box, Typography, alpha } from '@mui/material';
import { useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import { fNumber } from '../../../../../utils/formatNumber';
import modifyScriptName from '../../../../../utils/modifyScriptName';
import PriceWithColor from '../PriceWithColor';
import { ScriptCard, ChangeBox, PriceBox, PriceCard } from './StyledComponents';

// Background color is determined by PriceCard component based on price movement
// Text color is kept consistently readable across all backgrounds

const MobileWatchListRow = ({ item, onClick }) => {
  const quotesData = useSelector((state) => state?.trade?.quotesData[item?.instrument_token]);
  const prevData = useSelector((state) => state?.trade?.previousQuotesData?.[item?.instrument_token]);
  const marketStatus = useSelector((state) => state?.trade?.marketStatus);

  // Changed this line - Consider market as open by default for testing
  const isMarketClosed = marketStatus === 'closed';  // Removed !marketStatus condition

  // Memoize derived values to reduce recalculations
  const {
    isPositiveChange,
    lastPrice,
    bidPrice,
    askPrice,
    prevBidPrice,
    prevAskPrice,
  } = useMemo(() => {
    const isPositiveChange = +(quotesData?.chg || 0) >= 0;
    const lastPrice = quotesData?.last_price || 0;
    const bidPrice = +(quotesData?.depth?.buy?.[0]?.price || 0).toFixed(2) ||
      (+(quotesData?.ohlc?.high || 0) ? +(quotesData?.ohlc?.close || 0).toFixed(2) : 0);
    const askPrice = +(quotesData?.depth?.sell?.[0]?.price || 0).toFixed(2) ||
      (+(quotesData?.ohlc?.high || 0) ? +(quotesData?.ohlc?.close || 0).toFixed(2) : 0);

    // Calculate previous values from the Redux store
    const prevBidPrice = prevData ? (+(prevData?.depth?.buy?.[0]?.price || 0).toFixed(2) ||
      (+(prevData?.ohlc?.high || 0) ? +(prevData?.ohlc?.close || 0).toFixed(2) : 0)) : bidPrice;
    const prevAskPrice = prevData ? (+(prevData?.depth?.sell?.[0]?.price || 0).toFixed(2) ||
      (+(prevData?.ohlc?.high || 0) ? +(prevData?.ohlc?.close || 0).toFixed(2) : 0)) : askPrice;

    return {
      isPositiveChange,
      lastPrice,
      bidPrice,
      askPrice,
      prevBidPrice,
      prevAskPrice
    };
  }, [quotesData, prevData]);
  // References for previous prices to detect changes
  const prevBidRef = useRef(bidPrice);
  const prevAskRef = useRef(askPrice);

  // State for flashing animation
  const [bidFlashing, setBidFlashing] = useState(false);
  const [askFlashing, setAskFlashing] = useState(false);

  // State for tracking price movement direction - null means no change
  const [bidDirection, setBidDirection] = useState(() => {
    if (isMarketClosed) return null; // null for no change when market is closed

    // Always set an initial direction based on current vs previous
    if (prevData && bidPrice !== prevBidPrice) {
      return bidPrice > prevBidPrice;
    }

    // If no previous data or prices are the same, default to null for no color change
    return null; // Use null to get grey color for no change
  });

  const [askDirection, setAskDirection] = useState(() => {
    if (isMarketClosed) return null; // null for no change when market is closed

    // Always set an initial direction based on current vs previous
    if (prevData && askPrice !== prevAskPrice) {
      return askPrice > prevAskPrice;
    }

    // If no previous data or prices are the same, default to null for no color change
    return null; // Use null to get grey color for no change
  });

  useEffect(() => {
    // Skip the effect on first render to avoid duplicate animation
    const isFirstRender = prevBidRef.current === bidPrice;

    // Only reset to null if market is closed, otherwise keep current direction
    if (isMarketClosed) {
      setBidDirection(null);
      return;
    }

    // If it's the first render, just set the reference and skip animation
    if (isFirstRender) {
      prevBidRef.current = bidPrice;
      return;
    } const currentBid = Number(bidPrice);
    const previousBid = Number(prevBidRef.current);

    // If either value is invalid (0 or NaN), keep direction as null for neutral color
    if (!Number.isFinite(currentBid) || !Number.isFinite(previousBid)) {
      setBidDirection(null);
      return;
    }

    // If prices are exactly the same, set direction to null for neutral color
    if (currentBid === previousBid) {
      setBidDirection(null);
      return;
    }    // Compare and update direction
    const isIncreased = currentBid > previousBid;
    setBidDirection(isIncreased);
    setBidFlashing(true);

    const timerId = setTimeout(() => {
      setBidFlashing(false);
    }, 400); // 400ms for visual response

    // Update the reference after processing
    prevBidRef.current = currentBid;

    // Cleanup function to prevent memory leaks
    return () => {
      clearTimeout(timerId);
    };
  }, [bidPrice, isMarketClosed]);

  useEffect(() => {
    // Skip the effect on first render to avoid duplicate animation
    const isFirstRender = prevAskRef.current === askPrice;

    // Only reset to null if market is closed, otherwise keep current direction
    if (isMarketClosed) {
      setAskDirection(null);
      return;
    }

    // If it's the first render, just set the reference and skip animation
    if (isFirstRender) {
      prevAskRef.current = askPrice;
      return;
    } const currentAsk = Number(askPrice);
    const previousAsk = Number(prevAskRef.current);

    // If either value is invalid (0 or NaN), keep direction as null for neutral color
    if (!Number.isFinite(currentAsk) || !Number.isFinite(previousAsk)) {
      setAskDirection(null);
      return;
    }

    // If prices are exactly the same, set direction to null for neutral color
    if (currentAsk === previousAsk) {
      setAskDirection(null);
      return;
    }    // Simplified logic for ask price updates
    const isIncreased = currentAsk > previousAsk;
    setAskDirection(isIncreased);
    setAskFlashing(true);

    const timerId = setTimeout(() => {
      setAskFlashing(false);
    }, 400); // 400ms for visual response

    // Update the reference after processing
    prevAskRef.current = currentAsk;

    // Cleanup function to prevent memory leaks
    return () => {
      clearTimeout(timerId);
    };
  }, [askPrice, isMarketClosed]);  // Force null direction when market is closed, or update based on price comparison when open
  useEffect(() => {
    let mounted = true;

    if (isMarketClosed) {
      // Always set to null when market is closed for grey background
      if (mounted) {
        setBidDirection(null);
        setAskDirection(null);
      }
    } else {
      // For open market with no price changes yet, set directions based on current vs previous data
      // Only update directions that are currently null to avoid overwriting recent price movements
      if (bidDirection === null && mounted) {
        if (prevData && bidPrice !== prevBidPrice) {
          setBidDirection(bidPrice > prevBidPrice);
        }
        // Keep as null if there's no difference or no previous data
      }

      if (askDirection === null && mounted) {
        if (prevData && askPrice !== prevAskPrice) {
          setAskDirection(askPrice > prevAskPrice);
        }
        // Keep as null if there's no difference or no previous data
      }
    }

    return () => {
      mounted = false;
    };
  }, [isMarketClosed, bidDirection, askDirection, bidPrice, askPrice, prevBidPrice, prevAskPrice, prevData]);

  return (
    <ScriptCard onClick={() => onClick(item)}>
      <Grid container spacing={0.5} alignItems="center" justifyContent="space-between" sx={{
        borderRadius: 1,
        p: 0.4,
        '&:hover': {
          boxShadow: (theme) => `0 0 0 1px ${alpha(theme.palette.primary.main, 0.05)}`
        }
      }}>
        {/* Symbol and Change Information */}
        <Grid item xs={5}>
          <Box>
            <Typography variant="subtitle2"
              sx={{
                fontSize: 12.5,
                fontWeight: 600,
                color: (theme) => isPositiveChange ? theme.palette.success.dark : theme.palette.error.dark,
                letterSpacing: '0.2px',
                textTransform: 'uppercase'
              }}
              noWrap
            >
              {modifyScriptName(item.tradingsymbol, item.expiry)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.1 }}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: 11,
                  color: 'text.secondary',
                  fontWeight: 500
                }}
              >
                LTP: {fNumber(lastPrice)}
              </Typography>
              <ChangeBox isPositive={isPositiveChange}>
                <Icon
                  icon={isPositiveChange ? 'mdi:trending-up' : 'mdi:trending-down'}
                  width={9}
                  style={{ marginRight: 1 }}
                />
                {(quotesData?.chg || 0) < 0 ? '' : '+'}
                {fNumber(+(quotesData?.chg || 0).toFixed(2))}
              </ChangeBox>
            </Box>
          </Box>
        </Grid>
        {/* Bid Price with Card */}
        <Grid item xs={3.4}>
          <PriceCard
            isPositive={bidDirection}
            noChange={bidDirection === null}
            isFlashing={bidFlashing}
          >
            <PriceWithColor
              price={bidPrice}
              variant="subtitle2"
              sx={{
                fontWeight: 'bold',
                fontSize: '15px',
                transition: 'color 0.2s ease' // Reduced from 0.3s to 0.2s for faster response
              }}
              className="price-value"
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: '10px !important',
                display: 'block',
                fontWeight: 500
              }}
            >
              H: {fNumber(quotesData?.ohlc?.high || 0)}
            </Typography>
          </PriceCard>
        </Grid>
        {/* Ask Price with Card */}
        <Grid item xs={3.4}>
          <PriceCard
            isPositive={askDirection}
            noChange={askDirection === null}
            isFlashing={askFlashing}
          >
            <PriceWithColor
              price={askPrice}
              variant="subtitle2"
              sx={{
                fontWeight: 'bold',
                fontSize: '15px',
                transition: 'color 0.2s ease' // Reduced from 0.3s to 0.2s for faster response
              }}
              className="price-value"
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: '10px !important',
                display: 'block',
                fontWeight: 500
              }}
            >
              L: {fNumber(quotesData?.ohlc?.low || 0)}
            </Typography>
          </PriceCard>
        </Grid>
      </Grid>
    </ScriptCard>
  );
};

MobileWatchListRow.propTypes = {
  item: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired
};

export default React.memo(MobileWatchListRow);
